// no flow: util file, don't worry about type checking for now

import querystring from 'querystring';

import _ from 'lodash';

import { isEmptyString } from '@zapier/common-utils';

import _str from 'app/common/underscore-string';

const INVALID_IMAGE_CDN_URLS_REGEX = /cdn.zapier.com|zapier.cachefly.net/g;
const IMAGE_SERVICE_URL = 'images.zapier.com';

export const querystringToObj = qs => {
  qs = qs || window.location.search;
  if (qs[0] === '?') {
    qs = qs.substr(1);
  }
  return querystring.parse(qs);
};

const stripFromString = (regexOrString, string = '') =>
  string.replace(regexOrString, '');

export const stripLeadingSlash = _.partial(stripFromString, /^\/+/);
export const stripTrailingSlash = _.partial(stripFromString, /\/+$/);
export const stripTerminalSlashes = _.flow(
  stripLeadingSlash,
  stripTrailingSlash
);
export const ensureTrailingSlash = url => `${stripTrailingSlash(url)}/`;

export const deduplicateSlashes = url => url.replace(/\/{2,}/g, '/');

export const stripLeadingHash = hash => {
  return (hash || '').replace(/^#/, '');
};

const splitUrlPiece = (splitter, url) => {
  url = url || '';
  if (url.indexOf(splitter) < 0) {
    url = `${url}${splitter}`;
  }
  const splitterIndex = url.indexOf(splitter);
  return {
    baseHref: url.substring(0, splitterIndex),
    queryString: url.substring(splitterIndex + 1),
  };
};

export const splitUrl = _.partial(splitUrlPiece, '?');

export const splitHashParams = _.partial(splitUrlPiece, '#');

// Strips possible leading and trailing slashes and
// then adds them back to ensure they're always there
// and not doubled up. It's important to have the slashes
// to prevent something like `/dashboard` from matching
// `/dashboard-items` which is likely a completely
// different route.
// If the `retainHashOrQuery` argument is passed as truthy, then it
// will retain the hash or query string attached to the original `pathname`.
export const normalizePathname = (pathname, retainHashOrQuery = false) => {
  const query = splitUrl(pathname);
  const hash = splitHashParams(query.baseHref);

  const cleanPath = `/${stripTerminalSlashes(hash.baseHref)}/`;

  if (retainHashOrQuery) {
    const hashStr = _.isEmpty(hash.queryString) ? '' : `#${hash.queryString}`;
    const queryStr = _.isEmpty(query.queryString)
      ? ''
      : `?${query.queryString}`;

    return `${cleanPath}${queryStr}${hashStr}`;
  }

  return cleanPath;
};

export const isSubUrlOf = (url, maybeSubUrl) => {
  const cleanUrl = normalizePathname(url || '');
  const cleanSubUrl = normalizePathname(maybeSubUrl || '');
  return cleanUrl.indexOf(cleanSubUrl) > -1;
};

// Expects an array of paths that are blacklisted. Example:
// isUrlBlacklisted(url, ['/developer/']);
export const isUrlBlacklisted = (url, blacklistedRoutePrefixes) => {
  return blacklistedRoutePrefixes.some(path => isSubUrlOf(url, path));
};

// Remove the `from_url` query parameter from a url. Useful if you're adding a
// from_url so it doesn't keep appending.
export const removeFromUrl = href => {
  if (href.indexOf('?') < 0) {
    return href;
  }
  const { baseHref, queryString } = splitUrl(href);
  const queryStringParts = queryString.split('&');
  const cleanQueryStringParts = queryStringParts.filter(
    part => !_str.startsWith(part, 'from_url=')
  );
  const cleanQueryString = cleanQueryStringParts.join('&');
  if (cleanQueryString === '') {
    return baseHref;
  }
  return `${baseHref}?${cleanQueryString}`;
};

// Append `next` query parameter to a URL, if provided.
export const urlWithNext = (url, next) => {
  if (!next) {
    return url;
  }
  const { baseHref, queryString } = splitUrl(url);
  const separator = queryString ? '&' : '';
  const nextQuery = next ? `${separator}next=${encodeURIComponent(next)}` : '';
  return `${baseHref}?${queryString}${nextQuery}`;
};

export const makeQueryString = (oldParams, mergeParams) => {
  const newParams = _.extend({}, oldParams, mergeParams);
  return querystring.stringify(newParams);
};

const extractNonEmptyParams = queryString => {
  return _.pickBy(queryString, (val, key) => !!key);
};

export const extractParams = (url = '') => {
  return extractNonEmptyParams(querystring.parse(splitUrl(url).queryString));
};

export const extractParamsFromHash = (url = '') => {
  return extractNonEmptyParams(
    querystring.parse(splitHashParams(url).queryString)
  );
};

export const adjustUrlParams = (adjust, url) => {
  const parts = splitUrl(url);
  const newParams = adjust(extractParams(url)) || {};
  const newUrl = `${parts.baseHref}?${makeQueryString(newParams)}`;
  // Strip trailing `?` if there are no params
  return newUrl.replace(/\?$/, '');
};

export const addParamsToUrl = (url, params = {}) =>
  adjustUrlParams(currentParams => ({ ...currentParams, ...params }), url);

export const addTruthyParamsToUrl = (url, params = {}) => {
  const activeFields = _.omitBy(params, isEmptyString);
  return adjustUrlParams(
    currentParams => ({ ...currentParams, ...activeFields }),
    url
  );
};

export const removeParamsFromUrl = (url, paramsToRemove = []) => {
  return adjustUrlParams(
    currentParams => _.omit(currentParams, paramsToRemove),
    url
  );
};

export const addLocationOriginToUrl = (
  url,
  origin = window.location.origin
) => {
  const httpProtocolMatch = url.match(/https?:\/\//);
  if (httpProtocolMatch && httpProtocolMatch.index === 0) {
    return url;
  }

  return `${stripTrailingSlash(origin)}${url}`;
};

export const addParamsToHash = (url, params = {}) => {
  const queryString = makeQueryString(params);
  return `${url}#${queryString}`;
};

export const buildTextDataUrl = (content = '') =>
  `data:text/plain;charset=utf-8,${encodeURIComponent(content)}`;

export const prependProtocol = (url = '', protocol = 'https') =>
  `${protocol}://${url}`;

/* Utility method for image optimization */
export const buildOptimzedImageUrl = (src, width, height, format, background) =>
  _.isString(src) && src.match(INVALID_IMAGE_CDN_URLS_REGEX)
    ? addTruthyParamsToUrl(
        src.replace(INVALID_IMAGE_CDN_URLS_REGEX, IMAGE_SERVICE_URL),
        {
          dimensions: height ? `${width}x${height}` : width,
          format,
          background,
        }
      )
    : src;
